import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import type { XButton } from '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import '@adornis/forms/x-radiogroup.js';
import { Bold } from '@adornis/tiptap/client/extensions/Bold.js';
import { BulletList } from '@adornis/tiptap/client/extensions/BulletList.js';
import { createCollabExtensionByClass } from '@adornis/tiptap/client/extensions/Collab.js';
import { Color } from '@adornis/tiptap/client/extensions/Color.js';
import { Document } from '@adornis/tiptap/client/extensions/Document.js';
import { Dropcursor } from '@adornis/tiptap/client/extensions/Dropcursor.js';
import { HardBreak } from '@adornis/tiptap/client/extensions/HardBreak.js';
import { Highlight } from '@adornis/tiptap/client/extensions/Highlight.js';
import { Italic } from '@adornis/tiptap/client/extensions/Italic.js';
import { ListItem } from '@adornis/tiptap/client/extensions/ListItem.js';
import { LiteralTab } from '@adornis/tiptap/client/extensions/LiteralTab.js';
import { Paragraph } from '@adornis/tiptap/client/extensions/Paragraph.js';
import { Strike } from '@adornis/tiptap/client/extensions/Strike.js';
import { Text } from '@adornis/tiptap/client/extensions/Text.js';
import { TextStyle } from '@adornis/tiptap/client/extensions/TextStyle.js';
import { TransformerHTML } from '@adornis/tiptap/client/extensions/TransformerHTML.js';
import { Underline } from '@adornis/tiptap/client/extensions/Underline.js';
import '@adornis/tiptap/client/x-tiptap.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@adornis/writer/x-writer.js';
import type { XWriter } from '@adornis/writer/x-writer.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import type { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { transcribeFileIDAndSaveToAnswer } from '@ils/transcription/api/resolver.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { distinctUntilChanged, EMPTY, filter, firstValueFrom, map, switchMap, timer } from 'rxjs';
import { reportErrorOnAnswer } from '../../api/answer.js';
import { Answer, AnswerErrorReport, type AnswerError } from '../../db/answer.js';
import { ILSFile, ILSWebmFile } from '../../db/ils-files.js';
import type { ILSUser } from '../../db/ils-user.js';
import { ILSOwnNotes } from './ils-own-notes.js';

@customElement('ils-slot-detail')
export class ILSSlotDetail extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': { position: 'relative' },
      },
    ];
  }

  @property({ attribute: false }) examSlot = new RXController<Slot | undefined>(this, undefined);
  @property({ attribute: false }) rating?: ExamSlotRatingDoc;

  @property({ type: Boolean, attribute: 'rating-finished' }) ratingFinished?: boolean;

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());

  @state() public selectedMediaTab: 'media' | 'notes' = 'media';

  @state() public combinedAudio = 1;
  @state() private _transcribeAfterUpload = false;

  @state() private readonly _answer = new RXController(
    this,
    this.examSlot.observable.pipe(
      filter(Boolean),
      switchMap(examSlot =>
        //if examSlot doesn't have an answer, we call renderNoAnswerGiven() anyway. In that case we don't need the answer for the collabPlugin, as it is not rendered
        examSlot.answer?._id ? subscribeByID(Answer, examSlot.answer._id)({ _id: 1, text: 1 }) : EMPTY,
      ),
    ),
  );

  @state() private collabLoading = false;
  @state() private readonly collabPlugin = new RXController(
    this,
    this._answer.observable.pipe(
      filter(Boolean),
      distinctUntilChanged((prev, curr) => prev._id === curr._id),
      map(answer => {
        const extension = createCollabExtensionByClass(Answer._class, answer._id, {
          userProvider: this._currentUser.observable.pipe(
            filter(Boolean),
            map(user => ({ userID: user._id, username: user.ecommerce?.fullName ?? user.username })),
          ),
        });
        this.collabLoading = true; //we need to trigger a rerendering of the tiptap editor with the new extension/plugin
        setTimeout(() => (this.collabLoading = false), 300);
        return extension;
      }),
    ),
  );

  private readonly combinedAudioElem = createRef<HTMLAudioElement>();

  override render() {
    if (!this.examSlot.value) return nothing;
    if (!this._currentUser.value) return nothing;
    if (!this.examSlot.value.answer) return this.renderNoAnswerGiven();

    return html`
      <x-flex h100 flex space="md" ${css({ overflowY: 'auto', paddingBottom: this.spacing.xs })}>
        ${this.renderOperatorNotes()} ${this.renderMedia()}
      </x-flex>
    `;
  }

  private renderOperatorNotes(): Renderable {
    if (!this.examSlot.value?.operatorNotes) return nothing;

    return html`
      <x-flex
        space="xs"
        ${css({
          border: `1px solid ${this.colors.neutral.shade(100)}`,
          borderRadius: this.sizes.borderRadius,
          padding: this.spacing.sm,
        })}
      >
        <x-h3> Prüfungsnotizen </x-h3>
        <x-text> ${unsafeHTML(this.examSlot.value.operatorNotes)} </x-text>
      </x-flex>
    `;
  }

  private renderMedia(): Renderable {
    if (!this.examSlot.value?.answer) return nothing;
    if (this.examSlot.value.answer.type === 'voice') return this.renderMediaForVoice();
    return this.renderMediaForText();
  }

  private renderMediaForVoice(): Renderable {
    if (!this.examSlot.value?.answer?.fileId || !this.examSlot.value.answer.onlyTraineeFileId) return nothing;
    return html`
      <x-flex space="sm" w100 flex h100>
        <x-flex horizontal crossaxis-end space-between space="sm">
          <x-h4> Audio </x-h4>
          ${this.renderAudioOptions('combinedAudio')}
        </x-flex>
        <audio
          ${css({ width: '100%' })}
          src=${ILSWebmFile.getServeLink(this.examSlot.value.answer.fileId)}
          controls
          ${ref(this.combinedAudioElem)}
        ></audio>
        <x-flex
          flex
          space="md"
          space-between
          ${css({
            overflow: 'auto',
          })}
        >
          <div
            ${css({
              flex: '7 1 auto',
              overflow: 'auto',
              minHeight: '100px',
            })}
          >
            ${this.renderTranscriptInputFields()}
          </div>
          <div
            ${css({
              flex: '3 1 auto',
              overflow: 'auto',
              minHeight: '100px',
            })}
          >
            ${ILSOwnNotes({ rating: this.rating, currentUser: this._currentUser.value })}
          </div>
        </x-flex>
      </x-flex>
    `;
  }

  private renderMediaForText(): Renderable {
    if (!this.examSlot.value?.answer) return nothing;
    return html`
      <x-flex space="sm" w100 flex h100>
        <x-flex
          flex
          space="md"
          space-between
          ${css({
            overflow: 'auto',
          })}
        >
          <div
            ${css({
              flex: '7 1 auto',
              overflow: 'auto',
              minHeight: '100px',
            })}
          >
            <x-flex
              space="xs"
              ${css({
                border: `1px solid ${this.colors.neutral.shade(100)}`,
                borderRadius: this.sizes.borderRadius,
                padding: this.spacing.sm,
              })}
            >
              <x-h4> Textantwort </x-h4>
              ${this.examSlot.value.answer.text
                ? html` <x-text> ${unsafeHTML(this.examSlot.value.answer.text)} </x-text> `
                : html`
                    <x-flex horizontal crossaxis-center space="xs" ${css({ color: this.colors.tone.warning })}>
                      <x-icon> warning </x-icon> <x-text> Textantwort leer </x-text>
                    </x-flex>
                  `}
            </x-flex>
          </div>
          ${this.renderTextOptions()}

          <div
            ${css({
              flex: '3 1 auto',
              overflow: 'auto',
              minHeight: '80px',
            })}
          >
            ${ILSOwnNotes({ rating: this.rating, currentUser: this._currentUser.value })}
          </div>
        </x-flex>
      </x-flex>
    `;
  }

  private renderTextOptions(): Renderable {
    const answer = this.examSlot.value?.answer;
    if (!answer) return nothing;
    return html`
      <x-flex horizontal space="md" end ${css({ paddingBottom: this.spacing.xs })}>
        ${this.renderReportErrorButton()}
        ${permissionGuard({
          resource: 'answers',
          action: 'modify',
          guardedTemplate: html` <x-button
            mode="text"
            icon="edit"
            @click=${async (e: MouseEvent) => {
              const button = e.currentTarget as XButton;
              if (
                !(await XDialog.confirm(
                  'Diese Aktion wird den vorhandenen Text unwiderruflich löschen. Trotzdem fortfahren?',
                ))
              )
                return;

              const newText = await XDialog.showPopup<string>({
                content: (resolve, _, instance) => html`
                  <x-flex space="sm" padding="md">
                    <x-writer .value=${answer.text} ${css({ width: '500px' })}> </x-writer>
                    <x-button
                      tone="error"
                      icon="check"
                      @click=${() => {
                        const newText = (instance as XDialog<any>).renderRoot.querySelector<XWriter>('x-writer')?.value
                          .value;
                        resolve(newText ?? '');
                      }}
                    >
                      Überschreiben
                    </x-button>
                  </x-flex>
                `,
              }).catch(() => '');
              if (!newText) return;
              button.loading = true;
              answer.text = newText;
              try {
                await Promise.all([answer.save(), firstValueFrom(timer(500))]);
                void XSnackbar.show('gespeichert');
              } catch (error: any) {
                logger.error({ error }, 'error while saving');
                void XDialog.alert('Fehler beim Speichern: ' + error.message);
              }
              button.loading = false;
            }}
          >
            Bearbeiten
          </x-button>`,
        })}
      </x-flex>
    `;
  }

  private renderNoAnswerGiven(): Renderable {
    return html`
      <x-flex h100 flex space="md" ${css({ overflowY: 'auto', padding: `${this.spacing.sm} 0` })}>
        <x-text> Noch keine Antwort abgegeben. </x-text>
        ${permissionGuard({
          resource: 'answers',
          action: 'createempty',
          guardedTemplate: html`
            <x-button mode="outline" @click=${() => this._createEmptyAnswer()}> Leere Antwort anlegen </x-button>
          `,
        })}
      </x-flex>
    `;
  }

  private renderTranscriptInputFields(): Renderable {
    if (!this.collabPlugin.value) return nothing;
    if (this.collabLoading) return html` <x-flex center crossaxis-center> <x-loader></x-loader> </x-flex> `; //we need to trigger a rerendering of the tiptap editor with the new extension/plugin
    const enabled = hasPermission(this._currentUser.value, 'answers', 'modifytranscript');
    return html`
      <x-tiptap
        ?disabled=${!enabled}
        placeholder="Transkript"
        .extensions=${[
          [Document, Text, TextStyle, HardBreak, Dropcursor, Color, LiteralTab, ListItem, Paragraph(), BulletList],
          [Bold, Italic, Underline, Strike, Highlight],
          [TransformerHTML],
          this.collabPlugin.value.extension,
        ]}
      ></x-tiptap>
    `;
  }

  private renderAudioOptions(audioId: 'combinedAudio'): Renderable {
    const selected: number = this[audioId];
    return html`
      <x-flex horizontal crossaxis-center space="xs">
        ${this.renderReportErrorButton()}
        ${permissionGuard({
          resource: 'answers',
          action: 'modifytranscript',
          guardedTemplate: html`
            <x-button mode="icon" icon="speech_to_text" @click=${() => this.triggerTranscribe(audioId)}>
              <x-tooltip> <x-text> Transkribieren </x-text> </x-tooltip>
            </x-button>
          `,
        })}
        ${permissionGuard({
          resource: 'answers',
          action: 'modifyaudio',
          guardedTemplate: html`
            <x-button mode="icon" icon="edit" @click=${() => this.handleAudioUpload(audioId)}>
              <x-tooltip> <x-text> Upload </x-text> </x-tooltip>
            </x-button>
          `,
        })}
        <x-flex horizontal space="xxs">
          <x-button
            mode="icon"
            icon="undo"
            @click=${() => {
              const audioElem = (this[audioId + 'Elem'] as Ref<HTMLAudioElement>).value;
              if (audioElem) audioElem.currentTime -= 10;
            }}
          >
            <x-tooltip> <x-text> 10sek zurückspulen </x-text> </x-tooltip>
          </x-button>
          <x-button
            mode="icon"
            icon="redo"
            @click=${() => {
              const audioElem = (this[audioId + 'Elem'] as Ref<HTMLAudioElement>).value;
              if (audioElem) audioElem.currentTime += 10;
            }}
          >
            <x-tooltip> <x-text> 10sek vorspulen </x-text> </x-tooltip>
          </x-button>
        </x-flex>
        <x-icon> fast_forward </x-icon>
        <x-text
          ${css({
            cursor: 'pointer',
            background: selected === 1 ? this.colors.dark : this.colors.light,
            color: selected === 1 ? this.colors.on.dark : this.colors.on.light,
            padding: this.spacing.xxs,
            borderRadius: this.sizes.borderRadius,
          })}
          @click=${() => {
            const audioElem = (this[audioId + 'Elem'] as Ref<HTMLAudioElement>).value;
            if (audioElem) {
              audioElem.playbackRate = 1;
              this[audioId] = 1;
            }
          }}
        >
          1x
        </x-text>
        <x-text
          ${css({
            cursor: 'pointer',
            background: selected === 1.5 ? this.colors.dark : this.colors.light,
            color: selected === 1.5 ? this.colors.on.dark : this.colors.on.light,
            padding: this.spacing.xxs,
            borderRadius: this.sizes.borderRadius,
          })}
          @click=${() => {
            const audioElem = (this[audioId + 'Elem'] as Ref<HTMLAudioElement>).value;
            if (audioElem) {
              audioElem.playbackRate = 1.5;
              this[audioId] = 1.5;
            }
          }}
        >
          1.5x
        </x-text>
        <x-text
          ${css({
            cursor: 'pointer',
            background: selected === 2 ? this.colors.dark : this.colors.light,
            color: selected === 2 ? this.colors.on.dark : this.colors.on.light,
            padding: this.spacing.xxs,
            borderRadius: this.sizes.borderRadius,
          })}
          @click=${() => {
            const audioElem = (this[audioId + 'Elem'] as Ref<HTMLAudioElement>).value;
            if (audioElem) {
              audioElem.playbackRate = 2;
              this[audioId] = 2;
            }
          }}
        >
          2x
        </x-text>
      </x-flex>
    `;
  }

  private async handleAudioUpload(audioID: string) {
    if (
      !(await XDialog.confirm(
        html`<x-flex crossaxis-start space="md">
          <x-text>
            Das Hochladen einer neuen Audiodatei überschreibt vorhandene Audiodateien unwiderruflich! Trotzdem
            fortfahren?
          </x-text>
          <x-flex horizontal crossaxis-center space="xs">
            <x-checkbox
              label="Soll die Datei automatisch transkribiert werden? (Überschreibt aktuelles Transkript)"
              @value-picked=${(e: ValueEvent<boolean>) => (this._transcribeAfterUpload = e.detail.value)}
            ></x-checkbox>
          </x-flex>
        </x-flex>`,
      ))
    )
      return;
    if (!this.examSlot.value?.answer) return XDialog.alert('nothing to save into');

    const newFile = await askForFile({ acceptFileTypes: '.ogg,.webm' });
    if (!newFile?.[0]) return;
    const id = await XDialog.waitFor(
      ILSFile.upload(newFile[0], {
        acceptFileTypes: '.ogg,.webm',
        additionalInfo: {
          transcribeAfterUpload: this._transcribeAfterUpload ? 'true' : 'false',
          answerID: this.examSlot.value.answer._id,
        },
      }),
    );

    if (audioID === 'combinedAudio') this.examSlot.value.answer.fileId = id;
    else if (audioID === 'traineeAudio') this.examSlot.value.answer.onlyTraineeFileId = id;
    else throw new Error('audio ID is not combinedAudio or traineeAudio. Cant happen. Is: ' + audioID);

    try {
      await this.examSlot.value.answer.save();
      void XSnackbar.show('gespeichert');
    } catch (error: any) {
      logger.error({ error }, 'error while saving');
      void XDialog.alert('Fehler beim Speichern: ' + error.message);
    }
  }

  private async triggerTranscribe(audioID: 'traineeAudio' | 'combinedAudio') {
    if (!this.examSlot.value?.answer) return XDialog.alert('nothing to save into');
    const answerID = this.examSlot.value.answer._id;

    const fileID =
      audioID === 'traineeAudio' ? this.examSlot.value.answer.onlyTraineeFileId : this.examSlot.value.answer.fileId;
    if (!fileID) return XDialog.alert('no file to transcribe');

    await XNativeDialog.guard(() => transcribeFileIDAndSaveToAnswer(fileID, answerID)());
  }

  private async _createEmptyAnswer() {
    if (!this.examSlot.value?.osce) return;
    this.examSlot.value.answer = new Answer({
      slotID: this.examSlot.value._id,
      type: this.examSlot.value.osce.taskType,
      fileId: this.examSlot.value.osce.taskType === 'voice' ? 'pending' : undefined,
      onlyTraineeFileId: this.examSlot.value.osce.taskType === 'voice' ? 'pending' : undefined,
    });
    try {
      await this.examSlot.value.answer.create();
      this.requestUpdate('examSlot');
      void XSnackbar.show('gespeichert');
    } catch (error: any) {
      logger.error({ error }, 'error while saving');
      void XDialog.alert('Fehler beim Speichern: ' + error.message);
    }
  }

  private renderReportErrorButton(): Renderable {
    return html`${this.ratingFinished
      ? nothing
      : permissionGuard({
          resource: 'reportedErrors',
          action: 'create',
          guardedTemplate: html`
            <x-button
              mode="text"
              icon="report"
              tone="error"
              @click=${async () => {
                await this.handleReportError();
              }}
              >${this.scrollWidth < 354 /* width on HD, before scrollbar is popping up*/
                ? nothing
                : this.scrollWidth < 400
                ? 'Problem'
                : 'Problem melden'}
              <x-tooltip> <x-text> Problem mit der Antwort an die Admins melden </x-text> </x-tooltip>
            </x-button>
          `,
        })}`;
  }

  async handleReportError() {
    const answer = this.examSlot.value?.answer;
    if (!answer) return XNativeDialog.alert('nothing to report');
    if (answer.reportedError) return XNativeDialog.alert('Es wurde bereits ein Problem gemeldet');
    if (
      !(await XNativeDialog.confirm(
        'Wenn Sie ein Problem mit dieser Antwort melden werden Sie die Antwort nicht mehr bewerten können bis ein Admin diese Antwort überprüft. Trotzdem fortfahren?',
      ))
    )
      return;
    const report = await XNativeDialog.showPopup<AnswerErrorReport>({
      content: (resolve, reject, dialogElement) => {
        const reportData = new AnswerErrorReport({ reportedBy: this._currentUser.value?._id });
        reportData.errorType = answer.type === 'text' ? 'broken-text' : 'missing-trainee';
        return html`
          <x-flex padding="sm" space="md">
            <x-h4> Fehler melden </x-h4>
            ${answer.type === 'voice'
              ? html`
                  <x-radiogroup
                    .value=${reportData.errorType}
                    @value-picked=${(e: ValueEvent<AnswerError>) => (reportData.errorType = e.detail.value)}
                  >
                    <x-grid space="sm" columns="3">
                      <x-radiobutton label="Prüflingsaudio fehlt" selectable="missing-trainee"> </x-radiobutton>
                      <x-radiobutton label="Prüflingsaudio kaputt" selectable="broken-trainee"> </x-radiobutton>
                    </x-grid>
                  </x-radiogroup>
                `
              : html` <x-text bold> Problem mit Antworttext </x-text> `}
            <x-writer
              .value=${reportData.description}
              placeholder="Fehlerbeschreibung"
              @value-changed=${(e: ValueEvent<string>) => (reportData.description = e.detail.value)}
            >
            </x-writer>
            <x-flex horizontal space="md" space-between>
              <x-button mode="outline" @click=${() => reject()}> Abbrechen </x-button>
              <x-button
                tone="error"
                @click=${() => {
                  reportData.reportedAt = DateTime.now();
                  resolve(reportData);
                }}
              >
                Fehler melden
              </x-button>
            </x-flex>
          </x-flex>
        `;
      },
    });
    await XNativeDialog.waitFor(reportErrorOnAnswer(answer._id, report)());
    this.dispatchEvent(new CustomEvent('report-error'));
  }
}
